import { useQuery } from '@tanstack/react-query'
import { useFormContext } from 'react-hook-form'
import { CreateAppointmentSchema, Input } from '.'
import { appointmentMetadataOptions } from '$api-client/@tanstack/react-query.gen'
import { Text, Button, PriceInput, DurationPicker } from '$components'
import { useEffect, useState } from 'react'
import { PriceFormatter } from '$components'
import { TimeFormatter } from '$components'
import { DogBreed } from '$api-client/types.gen'

export function AppointmentMetadata() {
  const { watch, setValue, getValues } = useFormContext<CreateAppointmentSchema>()
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [duration, setDuration] = useState<number>(0)
  const [price, setPrice] = useState<number>(0)

  const createNewPet = watch(Input._NewPet)
  const newPetBreed = watch(Input.NewPetBreed)
  const newPetSizeCategory = watch(Input.NewPetSizeCategory)
  const newPetCoatType = watch(Input.NewPetCoatType)
  const pet = watch(Input.Pet)
  const services = watch(Input.Services)

  const { data: metadata } = useQuery({
    ...appointmentMetadataOptions({
      query: {
        pet_id: pet?.id ? Number(pet.id) : undefined,
        pet_breed: newPetBreed?.value as DogBreed,
        size_category_id: newPetSizeCategory?.id ? Number(newPetSizeCategory.id) : undefined,
        coat_type_id: newPetCoatType?.id ? Number(newPetCoatType.id) : undefined,
        services: services?.map(s => s.id),
      },
    }),
    enabled: !!(
      (pet || (createNewPet && newPetBreed)) &&
      services
    ),
  })

  useEffect(() => {
    if (metadata) {
      const { duration, price_in_cents } = metadata.data

      setValue(Input.Duration, Number(duration))
      setValue(Input.Price, Number(price_in_cents))
    }
  }, [metadata])

  useEffect(() => {
    if (isEditing) {
      setIsEditing(false)
    }

  }, [getValues(Input.Services)])

  const onSave = () => {
    if (!isEditing) {
      setDuration(getValues(Input.Duration))
      setPrice(getValues(Input.Price) / 100)
      setIsEditing(true)
      return
    }

    const currentDuration = getValues(Input.Duration)
    const currentSlot = getValues(Input.Slot)

    setValue(Input.Duration, duration)
    setValue(Input.Price, price * 100)

    // Clear selected slot if duration changed, as it's no longer valid
    if (currentDuration !== duration && currentSlot) {
      setValue(Input.Slot, null!)
    }

    setIsEditing(false)
  }

  if (!metadata) {
    return null
  }

  return (
    <div className="flex gap-stack-section">
      <div className="flex flex-col gap-stack-tight">
        <Text
          type="s"
          weight="medium"
        >
          Appointment Duration
        </Text>

        {isEditing
          ? (
            <DurationPicker
              duration={duration}
              onChange={setDuration}
              width="fit"
            />
          )
          : (
            <Text
              type="h2"
              weight="normal"
            >
              <TimeFormatter>
                {getValues(Input.Duration)}
              </TimeFormatter>
            </Text>
          )}
      </div>

      <div className="flex flex-col gap-stack-tight">
        <Text
          type="s"
          weight="medium"
        >
          Appointment Price
        </Text>

        {isEditing
          ? (
            <PriceInput
              name={Input.Price}
              currency={metadata.data.currency}
              value={price}
              onChange={(e) => setPrice(e.target.valueAsNumber)}
            />
          )
          : (
            <Text
              type="h2"
              weight="normal"
            >
              <PriceFormatter currency={metadata.data.currency}>
                {getValues(Input.Price)}
              </PriceFormatter>
            </Text>
          )}
      </div>

      <Button
        type="button"
        size="sm"
        variant="secondary"
        onClick={onSave}
        className="ml-auto"
      >
        {isEditing ? 'Save' : 'Edit'}
      </Button>
    </div>
  )
}
